import { useEffect, useMemo, useState } from 'react';

import {
    PRICE_CHANGE_SCOPE,
    PriceChangeScopeValue,
} from '../helpers/priceChangeScope';

export type ProductSalesViewScope = 'store' | 'wide';

export type ProductSalesScopeInput = {
    advertisingZoneId?: string;
    storeIds?: string[];
};

export type UseProductSalesScopeArgs = {
    advertisingZoneId?: string | null;
    priceChangeScope?: PriceChangeScopeValue | string | null;
    suggestedStoreIds?: string[] | null;
    lockToRequestScope?: boolean;
};

export type UseProductSalesScopeResult = {
    scope: ProductSalesViewScope;
    setScope: (scope: ProductSalesViewScope) => void;
    canToggle: boolean;
    scopeLabel: string;
    wideLabel: string;
    scopeInput: ProductSalesScopeInput | undefined;
};

function isPriceChangeScope(
    value: string | null | undefined,
): value is PriceChangeScopeValue {
    return (
        value === PRICE_CHANGE_SCOPE.STORE ||
        value === PRICE_CHANGE_SCOPE.SPECIFIC_STORES ||
        value === PRICE_CHANGE_SCOPE.ZONE
    );
}

function getDefaultViewScope(
    priceChangeScope: PriceChangeScopeValue | string | null | undefined,
): ProductSalesViewScope {
    if (
        priceChangeScope === PRICE_CHANGE_SCOPE.ZONE ||
        priceChangeScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES
    ) {
        return 'wide';
    }

    return 'store';
}

function getWideLabel(
    priceChangeScope: PriceChangeScopeValue | string | null | undefined,
    suggestedStoreCount: number,
): string {
    if (priceChangeScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
        return `Selected Stores (${suggestedStoreCount})`;
    }

    return 'Advertising Zone';
}

function buildScopeInput(
    viewScope: ProductSalesViewScope,
    advertisingZoneId: string | undefined,
    priceChangeScope: PriceChangeScopeValue | string | null | undefined,
    suggestedStoreIds: string[],
): ProductSalesScopeInput | undefined {
    if (viewScope === 'store') {
        return undefined;
    }

    if (
        priceChangeScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES &&
        suggestedStoreIds.length > 0
    ) {
        return advertisingZoneId
            ? { advertisingZoneId, storeIds: suggestedStoreIds }
            : { storeIds: suggestedStoreIds };
    }

    if (!advertisingZoneId) {
        return undefined;
    }

    return { advertisingZoneId };
}

export function useProductSalesScope({
    advertisingZoneId,
    priceChangeScope,
    suggestedStoreIds,
    lockToRequestScope = false,
}: UseProductSalesScopeArgs): UseProductSalesScopeResult {
    const zoneId = advertisingZoneId ?? undefined;
    const storeIds = useMemo(
        () =>
            (suggestedStoreIds ?? []).filter(
                (id): id is string => typeof id === 'string' && id.length > 0,
            ),
        [suggestedStoreIds],
    );
    const resolvedPriceChangeScope = isPriceChangeScope(priceChangeScope)
        ? priceChangeScope
        : priceChangeScope ?? null;

    const defaultScope = getDefaultViewScope(resolvedPriceChangeScope);
    const [scope, setScope] = useState<ProductSalesViewScope>(defaultScope);

    useEffect(() => {
        setScope(defaultScope);
    }, [zoneId, defaultScope]);

    const canToggle = !lockToRequestScope && Boolean(zoneId);
    const resolvedScope = canToggle ? scope : defaultScope;
    const wideLabel = getWideLabel(resolvedPriceChangeScope, storeIds.length);
    const scopeInput = buildScopeInput(
        resolvedScope,
        zoneId,
        resolvedPriceChangeScope,
        storeIds,
    );

    return {
        scope: resolvedScope,
        setScope,
        canToggle,
        scopeLabel: resolvedScope === 'wide' ? wideLabel : 'Store',
        wideLabel,
        scopeInput,
    };
}
